/**
 * Readable error text for Security Controls (spec E-43, E-51; design.md amendment rule 1; plan P8).
 *
 * Errors reach the tool through `apiClient`'s interceptor: an axios error with `response.status`
 * and a `serverError` property `{ code, message, details? }`. Two detail shapes exist: a list of
 * `{ path, message, code }` (zod path-shape failures) and an object of reason codes such as
 * `{ path: 'UNKNOWN_PATH' }` or `{ entries: 'NOT_AN_ACTIVE_SECURITY_ROLE', roleId }`.
 */
import {
  LIFECYCLE_FORBIDDEN,
  READABLE_EMPTY_ROLE_ID,
  READABLE_SYSTEM_ADMIN_ROLE,
  READABLE_TOO_MANY_DESCENDANTS,
  READABLE_UNKNOWN_PATH,
  REQUEST_FAILED,
  SAVE_CONFLICT,
  SAVE_FAILED_TITLE,
  SAVE_FORBIDDEN,
  SAVE_GENERIC,
  lifecycleFailedPrefix,
  readableDuplicateRole,
  readableNotActiveRole,
  saveInvalid,
} from './copy';
import type { EntryRow, LifecycleKind, MoveConflict } from './types';

export interface ApiErrorInfo {
  status?: number;
  code?: string;
  message?: string;
  details?: unknown;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const asString = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

/** Reads status, code, message and details from an unknown thrown value. */
export function readApiError(err: unknown): ApiErrorInfo {
  if (!isRecord(err)) return {};
  const response = isRecord(err.response) ? err.response : undefined;
  const status = typeof response?.status === 'number' ? response.status : undefined;
  const data = isRecord(response?.data) ? response.data : undefined;
  let server: Record<string, unknown> | undefined;
  if (isRecord(err.serverError)) server = err.serverError;
  else if (isRecord(data?.error)) server = data.error;
  return {
    status,
    code: asString(server?.code),
    message: asString(server?.message),
    details: server?.details,
  };
}

function roleNameFor(roleId: unknown, rows: readonly EntryRow[]): string {
  const id = asString(roleId);
  if (id === undefined) return 'A role';
  const key = id.toLowerCase();
  return rows.find((r) => r.roleId.toLowerCase() === key)?.roleName ?? id;
}

function readablePathCode(code: string, message: string | undefined): string | undefined {
  if (code === 'UNKNOWN_PATH') return READABLE_UNKNOWN_PATH;
  if (code === 'TOO_MANY_DESCENDANTS') return READABLE_TOO_MANY_DESCENDANTS;
  if (code === 'PROTECTED_PATH') return message;
  return undefined;
}

function readableEntriesCode(code: string, roleName: string): string | undefined {
  switch (code) {
    case 'SYSTEM_ADMIN_ROLE':
      return READABLE_SYSTEM_ADMIN_ROLE;
    case 'EMPTY_ROLE_ID':
      return READABLE_EMPTY_ROLE_ID;
    case 'NOT_AN_ACTIVE_SECURITY_ROLE':
      return readableNotActiveRole(roleName);
    case 'DUPLICATE_ROLE_ID':
      return readableDuplicateRole(roleName);
    default:
      return undefined;
  }
}

function readableFromObject(details: Record<string, unknown>, info: ApiErrorInfo, rows: readonly EntryRow[]): string | undefined {
  const pathCode = asString(details.path);
  if (pathCode !== undefined) {
    const text = readablePathCode(pathCode, info.message);
    if (text !== undefined) return text;
  }
  const entriesCode = asString(details.entries);
  if (entriesCode !== undefined) return readableEntriesCode(entriesCode, roleNameFor(details.roleId, rows));
  return undefined;
}

function readableFromList(details: unknown[]): string | undefined {
  const messages = details.map((d) => (isRecord(d) ? asString(d.message) : undefined)).filter((m): m is string => m !== undefined);
  return messages.length > 0 ? messages.join('; ') : undefined;
}

/** Both detail shapes as one readable sentence; falls back to the server message, then a generic one. */
export function readableDetails(err: unknown, rows: readonly EntryRow[] = []): string {
  const info = readApiError(err);
  let text: string | undefined;
  if (Array.isArray(info.details)) text = readableFromList(info.details);
  else if (isRecord(info.details)) text = readableFromObject(info.details, info, rows);
  return text ?? info.message ?? REQUEST_FAILED;
}

/** The idempotency key was reused with a different body: the caller must take a new key. */
export const isKeyMismatch = (err: unknown): boolean => readApiError(err).code === 'IDEMPOTENCY_KEY_MISMATCH';

/** E-51. */
export function saveFailureCopy(err: unknown, rows: readonly EntryRow[]): { title: string; message: string } {
  const info = readApiError(err);
  let message = SAVE_GENERIC;
  if (info.status === 403) message = SAVE_FORBIDDEN;
  else if (info.status === 409) message = SAVE_CONFLICT;
  else if (info.status === 400 && info.code === 'VALIDATION_ERROR') message = saveInvalid(readableDetails(err, rows));
  return { title: SAVE_FAILED_TITLE, message };
}

/** E-43 failure text, shown in the confirm panel's alert. */
export function lifecycleFailureText(kind: LifecycleKind, err: unknown): string {
  if (readApiError(err).status === 403) return LIFECYCLE_FORBIDDEN;
  const verb = kind === 'inactivate' ? 'Inactivate' : 'Activate';
  return `${lifecycleFailedPrefix(verb)} ${readableDetails(err)}`;
}

/** What a failed apply or dismiss was, from the status, the code and the details (C8, unit-10 amendments). */
export type MoveFailure =
  | { kind: 'forbidden' }
  | { kind: 'notPending' }
  | { kind: 'notFound' }
  | { kind: 'conflict'; conflicts: MoveConflict[] }
  | { kind: 'stale' }
  | { kind: 'inProgress' }
  | { kind: 'nothingToMove' }
  | { kind: 'notMovable'; which: 'oldPath' | 'newPath'; rule?: string }
  | { kind: 'other'; message?: string };

/** `error.details.conflicts` entries that have the three fields; anything else is dropped. */
function conflictsFrom(details: unknown): MoveConflict[] {
  const list = isRecord(details) && Array.isArray(details.conflicts) ? details.conflicts : [];
  return list.flatMap((c) => {
    if (!isRecord(c)) return [];
    const { roleId, roleName, path } = c;
    return typeof roleId === 'string' && typeof roleName === 'string' && typeof path === 'string' ? [{ roleId, roleName, path }] : [];
  });
}

const SIMPLE_MOVE_CODES: Readonly<Record<string, 'notPending' | 'notFound' | 'stale' | 'inProgress' | 'nothingToMove'>> = {
  MOVE_NOT_PENDING: 'notPending',
  MOVE_NOT_FOUND: 'notFound',
  MOVE_STALE: 'stale',
  MOVE_IN_PROGRESS: 'inProgress',
  MOVE_NOTHING_TO_MOVE: 'nothingToMove',
};

function notMovable(details: unknown): MoveFailure | undefined {
  if (!isRecord(details) || details.path !== 'MOVE_PATH_NOT_MOVABLE') return undefined;
  const { which } = details;
  if (which !== 'oldPath' && which !== 'newPath') return undefined;
  return { kind: 'notMovable', which, rule: asString(details.rule) };
}

export function moveFailureOf(err: unknown): MoveFailure {
  const info = readApiError(err);
  if (info.status === 403) return { kind: 'forbidden' };
  if (info.code === 'MOVE_CONFLICT') return { kind: 'conflict', conflicts: conflictsFrom(info.details) };
  const simple = info.code === undefined ? undefined : SIMPLE_MOVE_CODES[info.code];
  if (simple !== undefined) return { kind: simple };
  if (info.status === 400 && info.code === 'VALIDATION_ERROR') {
    const refused = notMovable(info.details);
    // Other validation failures (SAME_PATH, OVERLAP, PATH_TOO_LONG, ...) can be retried or dismissed.
    if (refused) return refused;
  }
  return { kind: 'other', message: clientErrorMessage(info) };
}

/**
 * The server's message is shown to the user only for a 4xx, where it describes the request (for
 * example SAME_PATH or OVERLAP). A 5xx message can carry internal detail (a SQL error, a stack
 * frame, a host name), and a network failure has no status at all, so both get the fixed sentence
 * alone (red-team finding 4).
 */
function clientErrorMessage({ status, message }: ApiErrorInfo): string | undefined {
  return status !== undefined && status >= 400 && status < 500 ? message : undefined;
}
